/**
 * Application error codes and types shared between backend and frontend.
 *
 * ConvexError data reaches the client in production, so messages and details
 * carried by this shape must be safe to show or log client-side.
 */
export const APP_ERROR_CODES = {
    /** Authenticated but not allowed to perform this operation */
    FORBIDDEN: "FORBIDDEN",
    /** Target entity does not exist or is soft-deleted; queries should usually return null */
    NOT_FOUND: "NOT_FOUND",
    /** State conflict, e.g. duplicate name or already-processed transition */
    CONFLICT: "CONFLICT",
    /** Optimistic-concurrency failure: client edited a stale version */
    STALE_VERSION: "STALE_VERSION",
    /** Input failed business validation beyond Convex validator shape checks */
    INVALID_ARGUMENT: "INVALID_ARGUMENT",
    /** Operation needs explicit user confirmation, usually for destructive actions */
    CONFIRMATION_REQUIRED: "CONFIRMATION_REQUIRED",
    /** Client exceeded a rate limit */
    RATE_LIMITED: "RATE_LIMITED",
    /** Upstream dependency unreachable or 5xx; app state unchanged, safe to retry */
    UPSTREAM_UNAVAILABLE: "UPSTREAM_UNAVAILABLE",
    /** Catalog provider or supporting infrastructure unavailable; safe admin guidance is attached */
    PROVIDER_UNAVAILABLE: "PROVIDER_UNAVAILABLE",
    /** Guardrail: operation would leave an org with zero active admins */
    LAST_ADMIN: "LAST_ADMIN",
    /** Assistant conversation is missing, archived, or inaccessible */
    THREAD_UNAVAILABLE: "THREAD_UNAVAILABLE",
    /** Unexpected internal failure surfaced deliberately with a safe message */
    INTERNAL_ERROR: "INTERNAL_ERROR",
    /** Persisted data violates an invariant and needs repair before retrying */
    DATA_INTEGRITY: "DATA_INTEGRITY",
    /** No Google Drive grant exists for this user in this organization */
    GOOGLE_DRIVE_NOT_CONNECTED: "GOOGLE_DRIVE_NOT_CONNECTED",
    /** A grant exists but Google no longer honours it; the user must connect again */
    GOOGLE_DRIVE_REAUTH_REQUIRED: "GOOGLE_DRIVE_REAUTH_REQUIRED",
    /** This deployment has no Google client, Picker key or WorkOS credentials configured */
    GOOGLE_DRIVE_NOT_CONFIGURED: "GOOGLE_DRIVE_NOT_CONFIGURED",
    /** Google refused the file: not granted, removed, or restricted by a Workspace policy */
    GOOGLE_DRIVE_FILE_NOT_ACCESSIBLE: "GOOGLE_DRIVE_FILE_NOT_ACCESSIBLE",
    /** Google refused the export because the exported document exceeds its size cap */
    GOOGLE_DRIVE_EXPORT_TOO_LARGE: "GOOGLE_DRIVE_EXPORT_TOO_LARGE",
    /** The Google type has no document export (Forms, Sites, Maps, shortcuts, folders) */
    GOOGLE_DRIVE_UNSUPPORTED_TYPE: "GOOGLE_DRIVE_UNSUPPORTED_TYPE",
    /** Google is rate limiting this client or user; safe to retry later */
    GOOGLE_DRIVE_RATE_LIMITED: "GOOGLE_DRIVE_RATE_LIMITED",
    /** The Drive file changed between the metadata read and the download */
    GOOGLE_DRIVE_SOURCE_CHANGED: "GOOGLE_DRIVE_SOURCE_CHANGED",
    /** A Google failure none of the codes above classifies */
    GOOGLE_DRIVE_ERROR: "GOOGLE_DRIVE_ERROR",
};
/**
 * App error codes that are expected, user-actionable outcomes: the UI surfaces them via
 * `getErrorMessage()` and the user can respond (fix the input, refresh, retry, confirm).
 * They are normal application flow, not defects, and neither Sentry project should page on them.
 *
 * Codes deliberately NOT listed stay captured because they signal bugs or infrastructure
 * problems: `FORBIDDEN` (the UI showed an affordance the user cannot use), `INTERNAL_ERROR`,
 * `DATA_INTEGRITY`, `PROVIDER_UNAVAILABLE`, `UPSTREAM_UNAVAILABLE`, and the two Google codes
 * nobody but an operator can act on — `GOOGLE_DRIVE_NOT_CONFIGURED` (a deployment missing its
 * Google client or Picker key) and `GOOGLE_DRIVE_ERROR` (a provider failure the taxonomy did
 * not recognise, which is the signal that the taxonomy needs a case).
 *
 * One list, two consumers, because the two error pipelines must agree on what "expected" means:
 *  - Frontend: `isExpectedAppError` (`apps/web-app/src/lib/convex-errors.ts`) → warning
 *    breadcrumb with `outcome: "expected_failure"`, no `captureError`, and a `beforeSend` drop
 *    (LAT-839).
 *  - Backend: `classifyTrackedError` (`packages/convex/convex/lib/tracked.ts`) → `warn` /
 *    `outcome: "expected_failure"` instead of an error-level wide event (LAT-910).
 */
export const EXPECTED_APP_ERROR_CODES = new Set([
    APP_ERROR_CODES.INVALID_ARGUMENT,
    APP_ERROR_CODES.CONFLICT,
    APP_ERROR_CODES.STALE_VERSION,
    APP_ERROR_CODES.CONFIRMATION_REQUIRED,
    APP_ERROR_CODES.RATE_LIMITED,
    APP_ERROR_CODES.NOT_FOUND,
    APP_ERROR_CODES.LAST_ADMIN,
    APP_ERROR_CODES.THREAD_UNAVAILABLE,
    // A Google connection, consent or Workspace-admin outcome the person can act on: connect,
    // reconnect, pick the file again, choose a smaller export, wait out a rate limit. The
    // provider refusing a caller who has not been granted anything is not a defect of ours.
    APP_ERROR_CODES.GOOGLE_DRIVE_NOT_CONNECTED,
    APP_ERROR_CODES.GOOGLE_DRIVE_REAUTH_REQUIRED,
    APP_ERROR_CODES.GOOGLE_DRIVE_FILE_NOT_ACCESSIBLE,
    APP_ERROR_CODES.GOOGLE_DRIVE_EXPORT_TOO_LARGE,
    APP_ERROR_CODES.GOOGLE_DRIVE_UNSUPPORTED_TYPE,
    APP_ERROR_CODES.GOOGLE_DRIVE_RATE_LIMITED,
    APP_ERROR_CODES.GOOGLE_DRIVE_SOURCE_CHANGED,
]);
/**
 * Whether `code` is an expected app-error code. Accepts `unknown` so callers can pass a value
 * read off untrusted error data without narrowing it first.
 */
export function isExpectedAppErrorCode(code) {
    return isAppErrorCode(code) && EXPECTED_APP_ERROR_CODES.has(code);
}
export function isAppErrorCode(value) {
    return (typeof value === "string" && Object.values(APP_ERROR_CODES).includes(value));
}
export function isAppErrorData(data) {
    if (!data || typeof data !== "object")
        return false;
    const obj = data;
    if (!("code" in obj) || !("message" in obj))
        return false;
    if (!isAppErrorCode(obj.code) || typeof obj.message !== "string")
        return false;
    if (!("details" in obj) || obj.details === undefined)
        return true;
    return isAppErrorDetails(obj.details);
}
function isAppErrorDetails(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        return false;
    return Object.values(value).every(isAppErrorDetailValue);
}
function isAppErrorDetailValue(value) {
    if (value === undefined || value === null)
        return true;
    if (["string", "number", "boolean"].includes(typeof value))
        return true;
    if (Array.isArray(value))
        return value.every(isAppErrorDetailValue);
    return isAppErrorDetails(value);
}
